import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import type { LossRateValues } from '~/pages/AdminLossRate/constants'
import {
  VOLTAGE_CLASSES,
  VOLTAGE_CLASS_I18N_KEYS,
} from '~/pages/AdminLossRate/constants'
import { formatLossRate } from '~/pages/AdminLossRate/models/format'

export type NextRevision = {
  effectiveDate: string
  values: LossRateValues
}

type Props = {
  areaLabel: string
  values: LossRateValues
  appliedDate: string
  next?: NextRevision
}

function formatDate(dateStr: string): string {
  return dateStr.replace(/-/g, '/')
}

// design `.lr-table th` (`css/components.css` .ecl-table 準拠 + 画面固有差分):
// 背景 Surface.tableHeader / 文字 mid-emphasis / 下線 Border.lowEmphasis。
const THEAD_BG = LightModeSemanticColors.Surface.tableHeader.value
const BORDER_LOW = LightModeSemanticColors.Border.lowEmphasis.value

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: THEAD_BG,
  color: LightModeSemanticColors.Text.midEmphasis.value,
  fontWeight: 600,
  fontSize: fontSizes.sm.value,
  borderBottom: `1px solid ${BORDER_LOW}`,
  verticalAlign: 'top',
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: `1px solid ${BORDER_LOW}`,
  fontSize: fontSizes.md.value,
}

const numTdStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
}

// design `.lr-table th + th, td + td { border-left }`: 2 列目以降の列区切り。
// これが無いと rowspan の「次回の改定予定はありません」が高圧の行への注記に
// 読める (2026-08-18 デザイナー指摘)。線があれば列全体にかかることが分かる。
const colSepStyle: React.CSSProperties = {
  borderLeft: `1px solid ${BORDER_LOW}`,
}

/** design `.lr-th-date`: TH 内の適用開始日サブ行 (sm / mid-emphasis / tabular-nums)。 */
const ThDate = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => (
  <Text
    as="span"
    textStyle="captionNormal"
    display="block"
    color="Text.midEmphasis"
    style={{ fontVariantNumeric: 'tabular-nums' }}
  >
    {children}
  </Text>
)

/**
 * 1 エリア分の損失率カード (design/admin/loss-rate.html `.lr-card` 準拠)。
 * 閲覧専用: 編集導線は持たない。
 *
 * 「現在の設定」 と「次回の設定」 を 1 つの表の 2 列として常時並列表示する
 * (issue #5684 / spec PR #3750 の PdM 決定: 改定予告バッジ + Accordion 開閉の
 * クリック展開方式は撤去し、 常時表示に統一)。 次回設定が値の据え置きでも
 * 表示する (AC-MLR-004)。 次回設定が未登録の場合は「次回の設定」 列に
 * rowspan で「次回の改定予定はありません」 を表示する (AC-MLR-005)。
 *
 * [spec差異] AC-MLR-003 / AC-MLR-004 は「現在の設定」 +「次回の設定」 の
 * 2 セクション構成を指定するが、 design HTML ([2026-08-18 #5684] コメント)
 * どおり 1 表 2 列に統合する。 2 セクションだと供給区分ヘッダー行が
 * カード内に 2 回出るうえ、 値が据え置きのエリアでは同じ数字を 2 度
 * 並べることになるため。 表示要素 (適用開始日 / 供給区分 × 損失率) は
 * AC どおりすべて出す。
 */
export const LossRateCard = ({
  areaLabel,
  values,
  appliedDate,
  next,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Box
      overflow="hidden"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      boxShadow="sm"
      backgroundColor="Surface.primary"
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        borderBottomWidth="1px"
        borderBottomColor="Border.lowEmphasis"
        // ヘッダー地はカード本体と同じ白 (Surface.primary)。 灰 (Surface.secondary)
        // だとページ地と同色で、カードの中の見出し帯に見えなかった
        // (2026-09-01 デザイナー指摘「light モードで白に見える primary を適用」)。
        backgroundColor="Surface.primary"
        paddingX={3}
        paddingY={3}
      >
        {/* 和文は任意位置で改行できるため、 flex の shrink が効くと min-content
            (= 1 文字幅) まで潰れて縦書きになる。 溢れる時は省略記号で畳む。 */}
        <Text
          textStyle="label"
          overflow="hidden"
          minWidth={0}
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {areaLabel}
        </Text>
      </Flex>

      {/* design `.table-scroll`: カード幅を割った時に列が黙って切り落とされない
          よう横スクロールへ逃がす。 */}
      <Box overflowX="auto">
        <TableRoot style={{ width: '100%', borderCollapse: 'collapse' }}>
          <TableHeader>
            <TableRow>
              <TableColumnHeader style={{ ...thStyle, width: '96px' }}>
                {t('page:adminLossRate.table.supplyCategory')}
              </TableColumnHeader>
              <TableColumnHeader
                style={{
                  ...thStyle,
                  ...colSepStyle,
                  textAlign: 'right',
                  width: '104px',
                }}
              >
                {t('page:adminLossRate.table.currentHeader')}
                <ThDate>
                  {t('page:adminLossRate.table.currentDate', {
                    date: formatDate(appliedDate),
                  })}
                </ThDate>
              </TableColumnHeader>
              <TableColumnHeader
                style={{
                  ...thStyle,
                  ...colSepStyle,
                  textAlign: 'right',
                  width: next !== undefined ? '104px' : 'auto',
                  whiteSpace: next !== undefined ? 'nowrap' : 'normal',
                }}
              >
                {t('page:adminLossRate.table.nextHeader')}
                {next !== undefined && (
                  <ThDate>
                    {t('page:adminLossRate.table.nextDate', {
                      date: formatDate(next.effectiveDate),
                    })}
                  </ThDate>
                )}
              </TableColumnHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {VOLTAGE_CLASSES.map((vc, index) => {
              // design `.lr-table tbody tr:last-child td` (L105-110): 最終行の
              // 下罫線は落とす (カード下端と二重線になるため。 実測: セル下端
              // 208px / カード枠 210px)。 rowspan セルは tr:first-child に属し
              // ながら下端は最終行と同じ位置なので、 同様に落とす。
              const lastRowStyle: React.CSSProperties =
                index === VOLTAGE_CLASSES.length - 1
                  ? { borderBottom: 'none' }
                  : {}
              return (
                <TableRow key={vc}>
                  {/* 電圧階級は折り返さない (UI 辞書 table.md の td は nowrap)。 en の
                      「Extra-high voltage」 が 96px で 2 行になり行の高さが揃わなかった */}
                  <TableCell
                    style={{
                      ...tdStyle,
                      whiteSpace: 'nowrap',
                      ...lastRowStyle,
                    }}
                  >
                    {t(VOLTAGE_CLASS_I18N_KEYS[vc])}
                  </TableCell>
                  <TableCell
                    style={{ ...numTdStyle, ...colSepStyle, ...lastRowStyle }}
                  >
                    {formatLossRate(values[vc])}
                  </TableCell>
                  {next !== undefined ? (
                    <TableCell
                      style={{ ...numTdStyle, ...colSepStyle, ...lastRowStyle }}
                    >
                      {formatLossRate(next.values[vc])}
                    </TableCell>
                  ) : (
                    // design `.lr-next-none` (AC-MLR-005): 未登録の旨を
                    // 「次回の設定」 列に rowspan で 1 度だけ表示する。
                    index === 0 && (
                      <TableCell
                        rowSpan={VOLTAGE_CLASSES.length}
                        style={{
                          ...tdStyle,
                          ...colSepStyle,
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          color: LightModeSemanticColors.Text.midEmphasis.value,
                          fontSize: fontSizes.sm.value,
                          borderBottom: 'none',
                        }}
                      >
                        {t('page:adminLossRate.card.nextNone')}
                      </TableCell>
                    )
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </TableRoot>
      </Box>
    </Box>
  )
}
